import { useMutateCreateProductPrl, useMutatePay } from "api/mutations";
import Button from "components/base/Button/Button";
import Dialog from "components/base/Dialog/Dialog";
import Input from "components/base/Input/Input";
import errorMessage from "constants/errorMessage";
import { COMPANY_FLOW_TYPE, PRL_FLOW_TYPE, PRODUCT_FLOW_TYPE } from "constants/flowTypes";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import YourOrder from "components/ui/YourOrder/YourOrder";
import createRegExpFromString from "utils/createRegExpFromString";

import Instruction from "components/base/Instruction/Instruction";
import useGetData from "hooks/api/useGetData";
import useGetQuote from "hooks/api/useGetQuote";
import useGetStatus from "hooks/api/useGetStatus";
import useGetPrice from "hooks/utils/useGetPrice";
import useGetSelectedPaymentMethodData from "hooks/utils/useGetSelectedPaymentMethodData";
import { renderLogo } from "utils/renderLogo";
import styles from "./styles.module.scss";
import getPayButtonTitle from "routes/Home/screens/SecondScreen/utils/getPayButtonTitle";
import { PrlObjectUpdateRequest } from "types/api/api";
import extractIdFromUrl from "utils/extractIdFromUrl";
import { useAppContext } from "context/AppContext";
import { BASE_URL_INTEGRATION } from "constants/url";

interface SecondScreenProps {
  setActiveStepIndex: (e: number) => void;
}

const SecondScreen = ({ setActiveStepIndex, ...rest }: SecondScreenProps) => {
  const [showModal, setShowModal] = useState(false);
  const { id = "123", type: flowType } = useParams();

  const appContext = useAppContext();
  const closeModal = () => setShowModal(false);
  const openModal = () => setShowModal(true);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    trigger: triggerForm,
    formState: { isValid, errors, isSubmitted }
  } = useForm({
    mode: "onTouched"
  });

  const { data: paymentMethodData } = useGetSelectedPaymentMethodData();
  const { data, prlData, productData, companyData, isLoading } = useGetData();
  const { isError: isErrorQuote, isLoading: isLoadingQuote, isSuccess: isSuccessQuote } = useGetQuote();
  const { isDone: isDoneStatus, refetch: refetchStatus } = useGetStatus({});

  const { totalPrice } = useGetPrice();
  const prlUuid = extractIdFromUrl(appContext?.companyPrlUrl);

  const { mutateAsync: mutatePay, isError: isErrorPay } = useMutatePay(
    flowType === COMPANY_FLOW_TYPE ? prlUuid : id
  );
  const { mutateAsync: mutateCreateProductPrl } = useMutateCreateProductPrl();

  const onSubmit = async (data: {
    nameFirst: string;
    nameLast: string;
    email: string;
    amount: number;
    phonenumber: string;
    companyName: string;
    companyAddress: string;
  }) => {
    closeModal();
    if (flowType === PRL_FLOW_TYPE) {
      // Send request to /pay
      await mutatePay({
        ...data,
        notes: "test",
        paymentMethod: appContext?.paymentMethod,
        accountNumber: appContext?.paymentInput
      });

      // Save data to local storage
      localStorage.setItem(
        id,
        JSON.stringify({
          paymentMethod: appContext?.paymentMethod,
          accountNumber: appContext?.paymentInput
        })
      );

      await refetchStatus();
    }

    if (flowType === PRODUCT_FLOW_TYPE) {
      const postData = {
        ...data,
        productId: productData?.id || 0,
        paymentMethod: appContext?.paymentMethod,
        accountNumber: appContext?.paymentInput,
        productQuantity: appContext.productQuantity,
        notes: "test"
      };

      const response = await mutateCreateProductPrl(postData);
      let url = response?.url;

      if (process.env.NODE_ENV === "development") {
        url = url?.replace(BASE_URL_INTEGRATION, "http://localhost:3000");
      }

      // Send user to PRL url
      window.location.href = url || "";
    }

    if (flowType === COMPANY_FLOW_TYPE) {
      const postData: PrlObjectUpdateRequest = {
        ...data,
        paymentMethod: appContext?.paymentMethod,
        accountNumber: appContext?.paymentInput,
        phonenumber: appContext?.paymentInput,
        companyAddress: companyData?.address?.text
      };

      // Trigger payment
      await mutatePay(postData);

      // Redirect user to status check after payment triggered
      let url = appContext?.companyPrlUrl;

      if (process.env.NODE_ENV === "development") {
        url = url?.replace(BASE_URL_INTEGRATION, "http://localhost:3000");
      }
      // Send user to PRL url
      window.location.href = url;
    }
  };

  // Navigator used for PRL flow
  useEffect(() => {
    if ((isSubmitted && isDoneStatus) || isErrorQuote || isErrorPay) {
      navigate(`/${flowType}/${id}/details`);
    }
  }, [isSubmitted, id, flowType, navigate, isDoneStatus, isErrorQuote, isErrorPay]);

  const onConfirmation = async () => {
    await handleSubmit(onSubmit)();
  };

  const handlePayButtonClick = async () => {
    await triggerForm();
    openModal();
  };

  const payButtonTitle = getPayButtonTitle({
    t,
    totalPrice,
    currency: prlData?.amount?.currency || productData?.amount?.currency
  });

  return (
    <>
      <div {...rest} className={styles.secondScreenWrapper} data-testid="second-screen_wrapper">
        {flowType !== COMPANY_FLOW_TYPE && (
          <>
            <div className="m-h2" data-testid="heading-first">
              {t("secondScreen.heading1")}
            </div>

            <div className={styles.oderWithFees}>
              {/* Your order is taking a quote information from the quoteQuery as user have NOT started a payment process yet */}
              {!isLoading && <YourOrder isLoading={isLoadingQuote} />}
            </div>
            <div className="m-divider-step-2"></div>
          </>
        )}

        <div className="d-flex align-items-center justify-content-center">
          <div className="m-container-medium">
            <div className="m-h2" data-testid="heading-second">
              {t("secondScreen.heading2")}
            </div>
            <form>
              <div className={styles.customerInformationBlock}>
                {data?.customer.map((field) => {
                  const pattern = createRegExpFromString(field.regex);
                  return (
                    <div className={styles.fieldWrapper} key={field.name}>
                      <Input
                        id={field.name}
                        message={errors[field.name] && errors[field.name].message}
                        key={field.name}
                        mandatory={field.mandatory}
                        error={!!errors[field.name]}
                        defaultValue={field.value}
                        label={field?.label?.find((l) => l.lang === i18n.language)?.text || ""}
                        placeholder={field?.description?.find((l) => l.lang === i18n.language)?.text || ""}
                        {...register(field.name, {
                          required: {
                            value: field.mandatory || false,
                            message: errorMessage.REQUIRED
                          },
                          pattern: {
                            value: pattern,
                            message: errorMessage.PATTERN
                          }
                        })}
                      />
                    </div>
                  );
                })}
              </div>

              <div className={styles.buttonWrapper}>
                <Button
                  disabled={!isValid || !isSuccessQuote}
                  variant="primary"
                  dataTestId={"pay-button"}
                  type="button"
                  onClick={handlePayButtonClick}
                  isLoading={isLoadingQuote}
                  title={payButtonTitle}
                />
              </div>
            </form>
            <div>
              <Button
                variant="secondary"
                title={t("secondScreen.buttonTitle2")}
                onClick={() => setActiveStepIndex(0)}
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal}>
        <Dialog message={t("secondScreen.titleConfimation")} onConfirm={onConfirmation} onCancel={closeModal}>
          {/* Your order is taking a quote information from the quoteQuery as user have NOT started a payment process yet */}
          <YourOrder isLoading={isLoadingQuote} />
          <div className={"mt-3"}></div>
          <Instruction
            imgUrl={renderLogo(appContext?.paymentMethod)}
            htmlContent={paymentMethodData?.instructions?.find((l) => l.lang === i18n.language)?.text}
          />
        </Dialog>
      </Modal>
    </>
  );
};

export default SecondScreen;
